import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import SignaturePad from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ClipboardCheck, Plus } from "lucide-react";

export default function Checklists() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role !== "auditor";

  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [newT, setNewT] = useState({ name: "", type: "opening", items: "" });
  const [active, setActive] = useState(null); // template being completed
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");

  const load = async () => {
    if (!activeSite) return;
    const [t, l] = await Promise.all([
      api.get("/checklists/templates", withSite(activeSite.id)),
      api.get("/checklists/logs", withSite(activeSite.id)),
    ]);
    setTemplates(t.data);
    setLogs(l.data);
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [activeSite]);

  const addTemplate = async (e) => {
    e.preventDefault();
    const items = newT.items.split("\n").map((x) => x.trim()).filter(Boolean);
    if (!items.length) return toast.error("Add at least one item");
    try {
      await api.post("/checklists/templates",
        { name: newT.name, type: newT.type, items },
        withSite(activeSite.id));
      toast.success("Template saved");
      setNewT({ name: "", type: "opening", items: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const startChecklist = (t) => {
    setActive(t);
    setResponses(Object.fromEntries(t.items.map((i) => [i, false])));
    setNotes("");
    setSignature("");
  };

  const submitChecklist = async () => {
    try {
      await api.post("/checklists/logs",
        { template_id: active.id, responses, notes, signature_data_url: signature },
        withSite(activeSite.id));
      toast.success("Checklist submitted");
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in" data-testid="checklists-page">
      <PageHeader
        title="Daily Checklists"
        subtitle="Opening, pre-operational and closing checks. Complete on a tablet — tick, add any notes, submit."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="checklists" />
            <PdfButton kind="checklists" />
          </div>
        }
      />

      <Tabs defaultValue="run">
        <TabsList className="mb-6">
          <TabsTrigger data-testid="tab-run" value="run">Run checklist</TabsTrigger>
          <TabsTrigger data-testid="tab-templates" value="templates">Templates</TabsTrigger>
          <TabsTrigger data-testid="tab-logs" value="logs">History</TabsTrigger>
        </TabsList>

        <TabsContent value="run">
          {!active ? (
            <div className="grid md:grid-cols-2 gap-4">
              {templates.length === 0 && (
                <div className="surface p-10 text-center text-charcoal-soft md:col-span-2">
                  No templates yet. Ask the admin to create one.
                </div>
              )}
              {templates.map((t) => (
                <div key={t.id} className="surface p-5 flex items-center justify-between">
                  <div>
                    <div className="label-eyebrow">{t.type.replace("_", " ")}</div>
                    <div className="font-display font-bold text-lg mt-1">{t.name}</div>
                    <div className="text-xs text-charcoal-soft mt-1">
                      {t.items.length} items
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      data-testid={`start-${t.id}`}
                      onClick={() => startChecklist(t)}
                      className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
                    >
                      Start
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="surface p-6 md:p-8 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-eyebrow">{active.type.replace("_", " ")}</div>
                  <h2 className="font-display font-bold text-2xl">{active.name}</h2>
                </div>
                <Button variant="ghost" onClick={() => setActive(null)}>Cancel</Button>
              </div>
              <div className="space-y-3">
                {active.items.map((item) => (
                  <label
                    key={item}
                    data-testid={`item-${item.slice(0, 16)}`}
                    className="flex items-start gap-3 p-4 surface-alt cursor-pointer hover:bg-eucalyptus-100"
                  >
                    <Checkbox
                      checked={responses[item] || false}
                      onCheckedChange={(v) => setResponses({ ...responses, [item]: Boolean(v) })}
                    />
                    <span className="text-sm">{item}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                <Label>Notes</Label>
                <Textarea data-testid="cl-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="mt-4">
                <SignaturePad onChange={setSignature} />
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  data-testid="cl-submit"
                  onClick={submitChecklist}
                  className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2"
                >
                  <ClipboardCheck className="w-4 h-4" /> Submit checklist
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates">
          {isAdmin && (
            <form onSubmit={addTemplate} className="surface p-6 mb-6 grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2 flex items-center gap-2">
                <Plus className="w-5 h-5 text-eucalyptus-700" />
                <h2 className="font-display font-bold text-lg">New template</h2>
              </div>
              <Input data-testid="t-name" placeholder="Name" value={newT.name}
                onChange={(e) => setNewT({ ...newT, name: e.target.value })} className="h-12" required />
              <Select value={newT.type} onValueChange={(v) => setNewT({ ...newT, type: v })}>
                <SelectTrigger data-testid="t-type" className="h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="opening">Opening</SelectItem>
                  <SelectItem value="closing">Closing</SelectItem>
                  <SelectItem value="pre_op">Pre-operational</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                data-testid="t-items"
                rows={6}
                placeholder="One checklist item per line..."
                value={newT.items}
                onChange={(e) => setNewT({ ...newT, items: e.target.value })}
                className="md:col-span-2"
              />
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" data-testid="t-submit"
                  className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
                  Add template
                </Button>
              </div>
            </form>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="surface p-5">
                <div className="label-eyebrow">{t.type.replace("_", " ")}</div>
                <div className="font-display font-bold mt-1">{t.name}</div>
                <ul className="mt-2 text-sm list-disc list-inside space-y-1 text-charcoal-soft">
                  {t.items.map((i) => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="logs">
          <div className="space-y-3">
            {logs.length === 0 && (
              <div className="surface p-10 text-center text-charcoal-soft">No completed checklists yet.</div>
            )}
            {logs.map((l) => {
              const total = Object.keys(l.responses || {}).length;
              const done = Object.values(l.responses || {}).filter(Boolean).length;
              return (
                <div key={l.id} className="surface p-5 flex items-center justify-between">
                  <div>
                    <div className="label-eyebrow">{l.template_name || "Checklist"}</div>
                    <div className="text-sm font-mono text-charcoal-soft mt-1">
                      {new Date(l.created_at).toLocaleString("en-AU")} · {l.user_name}
                    </div>
                    {l.notes && <div className="text-sm mt-1">{l.notes}</div>}
                  </div>
                  <Badge className="bg-eucalyptus-100 text-eucalyptus-900 hover:bg-eucalyptus-100 font-mono">
                    {done}/{total} done
                  </Badge>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
